import {
    __,
    at,
    compose,
    difference,
    get,
    isEmpty,
    keyBy,
    keys,
    map,
    mapValues,
    over,
    pick,
    set,
    spread,
    transform,
    update,
    values,
} from 'lodash/fp';
import { createSelector } from 'reselect';
import createCachedSelector from 're-reselect';
import { flow } from 'fp-ts/function';
import { TextKey, FeatureFlag, SearchType, RouteName } from '@mews-commander/enums';
import { getBooleanProp, getOrEmptyObject, getProp, getStringArrayProp, getStringProp } from '@mews/fp';
import { composeProps, createPropsExtender } from '@mews/framework';
import { ApplicationState, SearchResult } from '@mews-commander/types';
import { SearchResult as SearchResultCreator } from '@mews-commander/legacy-data-structures';
import { getContainer, getFeatureFlagService, getResolveRoute } from '@mews-commander/legacy-app-instance';
import { notEmpty, zipArgs } from '@mews-commander/utils';
import {
    getCurrentEnterpriseIanaTimeZoneIdentifier,
    getCurrentEnterpriseChainId,
    getCurrentEnterprise,
} from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { isCurrentEnterpriseServiceProviderWithBookableService } from '@mews-commander/legacy-entities/src/selectors/session/subscription';
import {
    isCurrentProfileEnabledToManageCustomers,
    isCurrentProfileEnabledToManageCompanies,
    canCurrentProfileManageReservations,
} from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { getCustomerCreateUrl, getReservationUrl } from '../../router/selectors';
import { moduleSelector } from './module';
import searchResultCreatorsMap from './searchResultCreators';

export const getAssistantEnabled = createSelector(getFeatureFlagService, (service) =>
    service.hasFlag(FeatureFlag.assistantEnabled)
);

const getSearch = createCachedSelector(
    getStringProp('scope'),
    moduleSelector,
    getOrEmptyObject
)(getStringProp('scope'));

const getSearchTerm = getStringProp('searchTerm');
export const isFetching = createSelector(getSearch, (search) => search.isFetching);
export const getResults = createSelector(getSearch, (search) => search.results || {});
export const getSelected = createSelector(getSearch, (search) => search.selected || {});

const createSearchTypeResultSelector = (type: $TSFixMe) => createSelector(getResults, getOrEmptyObject([type]));

export const [
    getBillResult,
    getCustomerResult,
    getEnterpriseProfileResult,
    getCompanyResult,
    getCityResult,
    getReservationResult,
    getReservationGroupResult,
    getResourceResult,
    getTravelAgencyResult,
    getResourceBlockResult,
    getLocationsResult,
    getNavigationResult,
    getProductServiceOrderResult,
] = map(createSearchTypeResultSelector)([
    SearchType.Bill,
    SearchType.Customer,
    SearchType.EnterpriseProfile,
    SearchType.Company,
    SearchType.City,
    SearchType.Reservation,
    SearchType.ReservationGroup,
    SearchType.Resource,
    SearchType.TravelAgency,
    SearchType.ResourceBlock,
    SearchType.Locations,
    SearchType.Navigation,
    SearchType.ProductServiceOrder,
]);

const getSearchResultCreatorOptions = createSelector(getCurrentEnterpriseIanaTimeZoneIdentifier, zipArgs(['timezone']));

const updateItems = (searchType: $TSFixMe, options: $TSFixMe) => {
    // @ts-expect-error $TSFixMeError: Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ Bill: (r: any) => a...
    const createSearchResult = searchResultCreatorsMap[searchType];
    return mapValues((item) => createSearchResult(item, options));
};

const createSearchResultSelector = (searchType: $TSFixMe, entityResultSelector: $TSFixMe) =>
    createSelector(entityResultSelector, getSearchResultCreatorOptions, (entities, options) =>
        compose(
            set('type', searchType),
            pick(['hasMore', 'isFetching', 'items']),
            update('items', updateItems(searchType, options))
            // @ts-expect-error $TSFixMeError: Argument of type 'unknown' is not assignable to parameter of type 'object'.
        )(entities)
    );

const filterExceptId = (searchResult: $TSFixMe, exceptIds: $TSFixMe) => {
    const itemKeys = keys(searchResult.items);
    const filteredItems = difference(itemKeys, exceptIds);
    return update('items', pick(filteredItems), searchResult);
};

const createFilteredSearchResultSelector = (selector: $TSFixMe) =>
    createSelector(selector, getStringArrayProp('except'), filterExceptId);

const prependNewEntityItem = (result: $TSFixMe, newEntityItem: $TSFixMe) => {
    const items = newEntityItem ? { newEntityItem, ...result.items } : result.items;

    return {
        ...result,
        items,
    };
};

const [
    getBillSearchResult,
    getCustomerSearchResult,
    getEnterpriseProfileSearchResult,
    getCompanySearchResult,
    getCitySearchResult,
    getReservationSearchResult,
    getReservationGroupSearchResult,
    getResourceSearchResult,
    getTravelAgencySearchResult,
    getResourceBlockSearchResult,
    getLocationsSearchResult,
    getNavigationSearchResult,
    getProductServiceOrderSearchResult,
] = map(spread(createSearchResultSelector))([
    [SearchType.Bill, getBillResult],
    [SearchType.Customer, getCustomerResult],
    [SearchType.EnterpriseProfile, getEnterpriseProfileResult],
    [SearchType.Company, getCompanyResult],
    [SearchType.City, getCityResult],
    [SearchType.Reservation, getReservationResult],
    [SearchType.ReservationGroup, getReservationGroupResult],
    [SearchType.Resource, getResourceResult],
    [SearchType.TravelAgency, getTravelAgencyResult],
    [SearchType.ResourceBlock, getResourceBlockResult],
    [SearchType.Locations, getLocationsResult],
    [SearchType.Navigation, getNavigationResult],
    [SearchType.ProductServiceOrder, getProductServiceOrderResult],
]);

const getBillResultSection = getBillSearchResult;

const getCustomerCreateBySearchTermUrl = composeProps(
    getCustomerCreateUrl,
    composeProps(
        createPropsExtender('chainId', getCurrentEnterpriseChainId),
        createPropsExtender('lastName', getSearchTerm)
    )
);

const getNewCustomerItem = createSelector(
    getBooleanProp('newEntity'),
    isCurrentProfileEnabledToManageCustomers,
    getContainer,
    getCustomerCreateBySearchTermUrl,
    (newEntity, hasPrivileges, container, customerUrl) =>
        newEntity &&
        hasPrivileges &&
        SearchResultCreator({
            searchType: SearchType.Customer,
            title: container().appLocalizationComponent.localizeText(TextKey.CustomerNew),
            url: customerUrl,
        })
);

const getCustomerResultSection = createSelector(
    createFilteredSearchResultSelector(getCustomerSearchResult),
    getNewCustomerItem,
    prependNewEntityItem
);

const getEnterpriseProfileResultSection = createFilteredSearchResultSelector(getEnterpriseProfileSearchResult);

const getCompanySearchResultCreator = createSelector(
    getCurrentEnterpriseChainId,
    getSearchTerm,
    getBooleanProp('newEntity'),
    isCurrentProfileEnabledToManageCompanies,
    getContainer,
    getResolveRoute,
    (chainId, term, newEntity, hasPrivileges, container, resolveRoute) =>
        newEntity &&
        hasPrivileges &&
        SearchResultCreator({
            searchType: SearchType.Company,
            title: container().appLocalizationComponent.localizeText(TextKey.PartnerCompanyNew),
            url: resolveRoute(RouteName.CompanyCreate, { query: { ownerId: chainId, name: term } }),
        })
);

const getCompanyResultSection = createSelector(
    createFilteredSearchResultSelector(getCompanySearchResult),
    getCompanySearchResultCreator,
    prependNewEntityItem
);

const getNewReservationResult = createSelector(
    getBooleanProp('newEntity'),
    canCurrentProfileManageReservations,
    getContainer,
    getReservationUrl,
    (newEntity, hasPrivileges, container, reservationUrl) =>
        newEntity &&
        hasPrivileges &&
        SearchResultCreator({
            searchType: SearchType.Reservation,
            title: container().appLocalizationComponent.localizeText(TextKey.ReservationNew),
            url: reservationUrl,
        })
);
const getReservationResultSection = createSelector(
    getReservationSearchResult,
    getNewReservationResult,
    prependNewEntityItem
);

const getReservationGroupResultSection = createFilteredSearchResultSelector(getReservationGroupSearchResult);

const getResourceResultSection = getResourceSearchResult;

const getTravelAgencyResultSection = getTravelAgencySearchResult;

const getResourceBlockResultSection = getResourceBlockSearchResult;

// @ts-expect-error $TSFixMeError: No overload matches this call.
const getLocationsSection = createSelector(getLocationsSearchResult, set('hasMore', false));

const getNavigationSection = getNavigationSearchResult;

const getCityResultSection = getCitySearchResult;

const getProductServiceOrderResultSection = getProductServiceOrderSearchResult;

const resultSectionSelectors = {
    [SearchType.Bill]: getBillResultSection,
    [SearchType.Customer]: getCustomerResultSection,
    [SearchType.EnterpriseProfile]: getEnterpriseProfileResultSection,
    [SearchType.Company]: getCompanyResultSection,
    [SearchType.City]: getCityResultSection,
    [SearchType.Reservation]: getReservationResultSection,
    [SearchType.ReservationGroup]: getReservationGroupResultSection,
    [SearchType.Resource]: getResourceResultSection,
    [SearchType.TravelAgency]: getTravelAgencyResultSection,
    [SearchType.ResourceBlock]: getResourceBlockResultSection,
    [SearchType.Locations]: getLocationsSection,
    [SearchType.Navigation]: getNavigationSection,
    [SearchType.ProductServiceOrder]: getProductServiceOrderResultSection,
};

const getSearchSelectors = (state: $TSFixMe, props: $TSFixMe) =>
    compose(
        // @ts-expect-error $TSFixMeError: Argument of type 'unknown' is not assignable to parameter of type 'Many<(...args: any[]) => unknown>'.
        (selectors) => over(selectors)(state, props),
        at(__, resultSectionSelectors),
        getProp<SearchType[]>()('searchTypes')
    )(state, props);

export const getResultSections = createSelector(
    getProp<SearchType[]>()('searchTypes'),
    getSearchSelectors,
    (searchTypes, resultSections) => {
        const sections = keyBy('type', resultSections);
        return transform(
            (acc, type) => {
                const section = compose(update('items', values), get([type]))(sections);

                if (notEmpty(section.items)) {
                    acc.push(section);
                }
            },
            [] as SearchResult[],
            searchTypes
        );
    }
);

export const getSelectedSearchResultItems = flow(getSearchSelectors, map('items'), (items) =>
    items.reduce((acc, newValue) => ({ ...acc, ...newValue }), {})
);

export const getSearchResult = createSelector<
    ApplicationState,
    { id: string; searchTypes: SearchType[]; scope: string },
    string,
    Record<string, SearchResult>,
    SearchResult | undefined
>(getStringProp('id'), getSelectedSearchResultItems, get);

export const getSelectedSearchResult = createSelector(getSelected, (selected: SearchResult) => {
    if (isEmpty(selected)) {
        return null;
    }

    return selected;
});

export const getPlaceholder = createSelector(getContainer, getCurrentEnterprise, (container, currentEnterprise) => {
    const { appLocalizationComponent } = container();

    let enterpriseName = appLocalizationComponent.localizeProperty((currentEnterprise as $TSFixMe).shortName);
    if (isEmpty(enterpriseName)) {
        enterpriseName = (currentEnterprise as $TSFixMe).name;
    }

    const placeholder = appLocalizationComponent.localizeText(TextKey.SearchInType, { type: enterpriseName });
    return placeholder;
});

export const getSearchTypes = createSelector(
    isCurrentEnterpriseServiceProviderWithBookableService,
    getAssistantEnabled,
    (isServiceProvider) => {
        const basicSearchTypes = [SearchType.Bill, SearchType.Customer, SearchType.Company];

        if (isServiceProvider) {
            const searchTypes: SearchType[] = [
                SearchType.Navigation,
                ...basicSearchTypes,
                SearchType.Reservation,
                SearchType.ReservationGroup,
                SearchType.Resource,
                SearchType.TravelAgency,
                SearchType.ResourceBlock,
                SearchType.ProductServiceOrder,
            ];
            return searchTypes;
        }
        return basicSearchTypes;
    }
);
